import React, { useState } from 'react';
import { BadgeCheck, Loader2, Mail } from 'lucide-react';
import { ApiError, sendEmailCode, verifyEmailCode } from '../../api';
import { useAuth } from '../../context/AuthContext';

interface Props {
  email: string;
  emailVerified: boolean;
}

const inputCls =
  'bg-field text-fg placeholder:text-fg-subtle w-full rounded-lg border border-default px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-600';

/** Card to view/change an account email and verify it with an emailed code. */
export default function EmailManager({ email: currentEmail, emailVerified }: Props) {
  const { setSession } = useAuth();
  const [email, setEmail] = useState(currentEmail);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const unchangedAndVerified =
    email.trim().toLowerCase() === currentEmail.toLowerCase() && emailVerified;

  const sendCode = async () => {
    setError('');
    setInfo('');
    setBusy(true);
    try {
      await sendEmailCode(email.trim());
      setCodeSent(true);
      setInfo(`Verification code sent to ${email.trim()}.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send a code.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setBusy(true);
    try {
      const session = await verifyEmailCode(code.trim());
      setSession(session);
      setCode('');
      setCodeSent(false);
      setInfo('Email verified.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Invalid or expired code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
      <div className="border-subtle flex items-center justify-between gap-3 border-b px-4 py-4 sm:px-6 sm:py-5">
        <div>
          <h2 className="text-fg text-base font-bold sm:text-lg">Email</h2>
          <p className="text-fg-muted mt-0.5 hidden text-xs sm:block sm:text-sm">
            Verifying your email keeps your account secure
          </p>
        </div>
        {unchangedAndVerified ? (
          <span className="bg-success-soft text-success flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium">
            <BadgeCheck className="h-3.5 w-3.5" />
            Verified
          </span>
        ) : (
          <span className="bg-warn-soft text-warn shrink-0 rounded-full px-2.5 py-1 text-xs font-medium">
            Unverified
          </span>
        )}
      </div>
      <div className="space-y-3 px-4 py-4 sm:px-6">
        {error && <p className="text-danger text-sm font-medium">{error}</p>}
        {info && <p className="text-success text-sm font-medium">{info}</p>}
        <div>
          <label
            htmlFor="account-email"
            className="text-fg-secondary mb-1 block text-xs font-semibold tracking-wide uppercase"
          >
            Email address
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Mail className="text-fg-subtle absolute top-2.5 left-2.5 h-4 w-4" />
              <input
                id="account-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setCodeSent(false);
                }}
                className={`${inputCls} pl-8`}
              />
            </div>
            {!unchangedAndVerified && (
              <button
                type="button"
                onClick={sendCode}
                disabled={busy || !email.trim()}
                className="border-default text-fg-secondary hover:bg-canvas shrink-0 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {codeSent ? 'Resend code' : 'Send code'}
              </button>
            )}
          </div>
        </div>

        {codeSent && !unchangedAndVerified && (
          <form onSubmit={verify} className="flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              className={`${inputCls} tracking-[0.3em]`}
            />
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="bg-brand-600 hover:bg-brand-700 flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Verify
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
